import React from "react";
import ReactDOM from "react-dom";
import { t } from "../utils/i18n";
import { MapPin, Users, Repeat, User } from "lucide-react";

export interface PublishIdentity {
  type: "user" | "venue" | "community" | "ritual";
  id: string | null;
  name: string;
  avatar_url: string | null;
  verified: boolean;
  subtitle?: string;
  role: string;
  // Uniquement présent pour type==="venue" — 'pro'/'association' débloque les Pro Events dans
  // Create.tsx, 'lieu' (ou absent) ne les propose jamais.
  venueSubtype?: string;
}

// Sélecteur "Publier en tant que" — même recette de bottom sheet que RealVibeMoodSheet.tsx
// (fond blanc, coins arrondis en haut uniquement, X discret). Une ligne par identité au lieu
// d'une grille : "Personnel" toujours en premier, puis chaque lieu/communauté/rituel administré.
// Sélectionner une option ferme le sheet immédiatement, pas de bouton "valider" séparé.
export default function PublishAsSheet({
  lang, identities, selected, onSelect, onClose, onManageIdentities,
}: {
  lang: string;
  identities: PublishIdentity[];
  selected: PublishIdentity;
  onSelect: (identity: PublishIdentity) => void;
  onClose: () => void;
  onManageIdentities: () => void;
}) {
  const iconFor = (type: string) => (type === "venue" ? MapPin : type === "community" ? Users : type === "ritual" ? Repeat : User);

  // Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un position:fixed
  // descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:"touch") se positionne
  // sur Safari iOS par rapport au contenu défilant plutôt qu'au vrai viewport.
  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 3000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxWidth: 480, maxHeight: "80vh", overflowY: "auto", boxSizing: "border-box" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 19, fontWeight: 800 }}>{t(lang, "publish_as_title")}</div>
          <button onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999", padding: 0, lineHeight: 1 }}>✕</button>
        </div>

        {identities.map(idn => {
          const Icon = iconFor(idn.type);
          const isSelected = selected.type === idn.type && selected.id === idn.id;
          const subtitle = idn.type === "user" ? t(lang, "publish_as_personal_sub") : t(lang, `publish_as_type_${idn.type}`) + (idn.subtitle && idn.subtitle !== idn.name ? ` · ${idn.subtitle}` : "");
          return (
            <button
              key={`${idn.type}-${idn.id ?? "self"}`}
              onClick={() => { onSelect(idn); onClose(); }}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 12,
                padding: "14px 16px", borderRadius: 16, marginBottom: 8, textAlign: "left", cursor: "pointer",
                border: isSelected ? "1.5px solid #b8952e" : "1px solid #eee5d0",
                backgroundColor: isSelected ? "#faf8f3" : "white",
              }}
            >
              <div style={{ width: 38, height: 38, borderRadius: "50%", backgroundColor: "#f3f3f3", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {idn.avatar_url ? (
                  <img src={idn.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <Icon size={18} color="#0a0a0a" strokeWidth={1.8} />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {idn.type === "user" ? t(lang, "publish_as_personal") : idn.name}
                </div>
                <div style={{ fontSize: 12, color: "#999" }}>{subtitle}</div>
              </div>
            </button>
          );
        })}

        <button
          onClick={() => { onClose(); onManageIdentities(); }}
          style={{ width: "100%", textAlign: "center", padding: "10px 0", marginTop: 8, border: "none", background: "none", color: "#999", fontSize: 13, cursor: "pointer" }}
        >
          {t(lang, "publish_as_manage")}
        </button>
      </div>
    </div>
  ,
    document.body
  );
}
